import {bindable} from "aurelia-templating";
import {I18N} from "aurelia-i18n";
import {computedFrom} from "aurelia-binding";
import {Reflection} from "../../../utilities/reflection";
import {autoinject} from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { EventAggregator } from "aurelia-event-aggregator";
import { Disposable } from "aurelia-framework";
import { Messages } from "../../../configuration/event-messages.constants";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";

@autoinject
export class RatingSelector {

    @bindable
    public selectedItemIds: number[] = [];

    @bindable
    public propertyName: string = "";

    @bindable
    public endLabel: string = "";

    @bindable
    public min: number = 0;

    @bindable
    public max: number = 5;

    @bindable
    public externalLabel: string;

    @bindable
    public startExternalLabel: string;

    @bindable
    public mobileTitle: string;

    @bindable
    public mobileDescription: string;

    @bindable
    public showRangeValues: boolean = false;

    @bindable
    public showCommas: boolean = true;
    public ratingModels: RatingItem[];
    public previousRatingModel: RatingItem[];

    @bindable
    public showActionButtons: boolean = true;
    
    @bindable
    public closePopUpOnClear: boolean = true;

    @bindable
    public isMobile: boolean = false;

    selectAll: boolean;
    public isApplied: boolean = false;

    selectAllChanged(value : boolean){
        if(value){
            this.ratingModels.forEach(x => x.isSelected = true);
        }
        else{
            this.ratingModels.forEach(x => x.isSelected = false);
        }
        this.updateSelectedItemIds();
        this.updateExternalLabel();
    }

    constructor(private element: Element, private i18N: I18N, private eventAggregator:EventAggregator) { }

    _subscriptionToDocumentClickEvent: Disposable;
    attached() {
        setTimeout(() => {
            this.isOpen = true;
            this.previousRatingModel = CloneHelper.cloneObject(this.ratingModels)
            this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup.bind(this));
        });

        if(this.selectedItemIds && this.selectedItemIds.length > 0)
        {
            this.isApplied = true;
        }
        this.updateExternalLabel();
    }

    detached() {
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    isOpen:boolean;
    closePopup(x: any) {
        if(this.isMobile) return;

        //Includes 'this.showActionButtons' so that when clicking outside to close the dropdown, when having action buttons 
        //not showing, won't reset the selected ids DEL-14737
        if (x && x.source == 'document' && this.isOpen && this.showActionButtons) {
            this.isOpen = false;
            this.applyFilters(true, true);
        }
    }

    updateSelectedItemIds(){
        this.selectedItemIds = Enumerable.from(this.ratingModels).where(x=>x.isSelected).select(x=>x.rating).toArray();

        if (this.ratingModels.filter(x => !x.isSelected).length > 0) {
            this.selectAll = false;
        }
    }

    updateRatingModelsSelected(selectedItemIds: number[]){
        if(!selectedItemIds || selectedItemIds.length == 0)
        {
            this.ratingModels.forEach(x => {
                x.isSelected = false;
            });
            this.selectAll = false;
        }
        else{
            selectedItemIds.forEach(selectedItem =>{
                if(typeof selectedItem == "number") {
                    const itemMatchedWithSelected = this.ratingModels.filter(x=> x.rating == selectedItem)[0];
                    itemMatchedWithSelected ? itemMatchedWithSelected.isSelected = true : itemMatchedWithSelected.isSelected = false
                }
            });
        }
    }

    selectedItemIdsChanged(){
        this.updateRatingModelsSelected(this.selectedItemIds);

        this.updateExternalLabel();
    }

    maxChanged(){
        this.setRatingsModels(false);
    }

    @computedFrom(Reflection.getPropertyPath<RatingSelector>(x => x.propertyName))
    public get label() {
        return this.i18N.tr('sliders-label-text').replace("{0}", this.propertyName ? this.propertyName : this.i18N.tr('value-text'));
    }

    propertyNameChanged() {
        this.updateExternalLabel();
    }

    getSelectionDescription(isForMobile: boolean = false){
        const allSelected = Enumerable.from(this.ratingModels).all(x=>x.isSelected);
        const selectedRatings = Enumerable.from(this.ratingModels).where(x=>x.isSelected).orderBy(x=>x.rating).select(x=>x.rating).toArray();

        let joinedString = ""
        selectedRatings.forEach(x => {
            joinedString += x == -1? "NA, " : `${x}, `
        });
        joinedString = joinedString.slice(0, joinedString.length-2)
        const starsLabel = `(${joinedString})`;

        if(allSelected) return `${this.startExternalLabel} (All)`;
        return (!this.isApplied && this.showActionButtons) && !isForMobile ?
                (this.startExternalLabel ? `${this.startExternalLabel}` : `Ratings`)
                : (this.startExternalLabel ? `${this.startExternalLabel} ${starsLabel}` : `Ratings - ${starsLabel}`);
    }

    updateExternalLabel() {
        if (Enumerable.from(this.ratingModels).any(x => x.isSelected)) {
            this.externalLabel = this.getSelectionDescription();
            this.mobileTitle = this.propertyName;
            this.mobileDescription = this.getSelectionDescription(true);
        }
        else {
            this.externalLabel = "";
            this.mobileTitle = this.propertyName;
            this.mobileDescription = "";
        }
    }

    public clear() {
        this.reset();
        this.setRatingsModels(false);
        
        if(this.closePopUpOnClear) {
            setTimeout(() => {
                this.applyFilters(false);
            }, 120);
        }
        
        this.updateExternalLabel();
    }

    public applyFilters(closeFilter: boolean = true, reset: boolean = false) {
        if(reset) {
            this.ratingModels = this.previousRatingModel;
            this.reset();
        }

        this.isApplied = Enumerable.from(this.ratingModels).any(x=>x.isSelected);
        this.updateExternalLabel();
        const changeEvent: CustomEvent = new CustomEvent("apply-filters", { bubbles: true, detail: { closeFilter: closeFilter, filters: this.ratingModels.filter(x=>x.isSelected) } });
        this.element.dispatchEvent(changeEvent);
    }

    setRatingsModels(isSelected: boolean) : RatingItem[]{
        const arr = [];

        for (let _i = 0; _i <= this.max; _i++) {
            arr.push(new RatingItem(isSelected, _i, -3));
        }
        arr.push(new RatingItem(isSelected, -1, -1));
        this.ratingModels = arr;
        return this.ratingModels;
    }
    
    reset() {
        this.selectedItemIds = [];
        this.selectAll = false;
    }
}

export class RatingItem {
    constructor(isSelected: boolean, rating:number, ratingType:number){
        this.isSelected = isSelected;
        this.rating = rating;
        this.ratingType = ratingType;
    }
    isSelected: boolean = true;
    rating: number;
    ratingType: number;
}